(async () => {
    const { Core } = await importModule('Core');
    const { Routes, parseHTML } = await importModule('Utils');
    
    const CART_R_SECTION = 'r_cart';
    const NOTIFICATION_SECTION = 'r_cart-notification';
    const ATTR_IS_CART_PAGE = 'cart-page';
    const ATTR_ERROR_TEXT = 'error-text';
    const SECTION_DRAWER = 'g_cart-drawer';

    // Cart AJAX API errors (422 over-inventory, 404 stale line key, ...) carry a
    // message/description pair we surface verbatim. Anything else (network drop,
    // 5xx, malformed body) falls back to the generic translated string.
    class CartError extends Error {
        constructor(title = '', description = '') {
            super(title || description || 'Cart request failed');
            this.title = title;
            this.description = description;
        }
    }

    customElements.define('cart-provider', class extends Core {
        subscriptions = {
            'cart:add-feature': '_addFeature',
            'cart:add': '_add',
            'cart:change': '_update',
            'cart:clear': '_clear'
        }

        features = {};

        render() {
            this.subscribe('cart:add-feature', { global: true });
            this.subscribe('cart:add', { global: true });
            this.subscribe('cart:change', { global: true });
            this.subscribe('cart:clear', { global: true })
        }

        _addFeature({ feature }) {
            this.features[feature] = true;
        }
        
        // `attributes` (optionnel) : attributs de panier à poser avec l'ajout
        // (ex. `kits`, pour l'analytique) ; ils survivent au nettoyage de
        // notification_items_variant_ids ci-dessous.
        async _add({ items, attributes }) {
            try {
                const resBody = {items};
                if (attributes) {
                    resBody.attributes = { ...attributes };
                }
                if (this.features['notification']) {
                    resBody.sections = [NOTIFICATION_SECTION];
                    resBody.attributes = {
                        ...resBody.attributes,
                        notification_items_variant_ids: items.map(({id}) => id).join(',')
                    };
                }
                const res = await this._requestCart('cartAdd', resBody);

                this._cleanCartAttributes();
                
                if (this.redirectToCart) {
                    window.location.pathname = Routes('cart');
                    return;
                }

                const cartData = await res.json();
                if(!res.ok) {
                    throw new CartError(cartData.message, cartData.description);
                }

                await this._publishUpdates(cartData);
            } catch (e) {
                this._handleError(e);
            }
        }

        _cleanCartAttributes() {
            if(this.features['notification']) {
                this._requestCart('cartUpdate', { attributes: { notification_items_variant_ids: null }});
            }
        }

        async _update({ key, quantity, selling_plan }) {
            try {
                const res = await this._requestCart('cartChange', {
                        id: key, quantity, selling_plan,
                        sections: [CART_R_SECTION],
                        sections_url: Routes('cart')
                    }
                );

                const cartData = await res.json();
                if(!res.ok) {
                    throw new CartError(cartData.message, cartData.description);
                }

                await this._publishUpdates(cartData);
            } catch (e) {
                this._handleError(e);
            }
        }

        // Every failure path must end here: 'cart:error' is what releases the
        // spinner overlay on the line item, so swallowing an exception silently
        // leaves the row locked until a reload.
        _handleError(e) {
            console.error(e);

            const title = e instanceof CartError && e.title ? e.title : this.errorText;
            const message = e instanceof CartError ? e.description : '';

            if (title || message) {
                this.publish('toast-notification:open', { title, message, type: 'error' });
            }

            this.publish('cart:error');
        }

        async _publishUpdates(cartData) {
            const cartSection = cartData.sections?.[CART_R_SECTION];

            // The rendered line items already came back with this response, so paint
            // them now rather than behind the meta/drawer roundtrips below. Only the
            // cart page listens for 'cart:render'; everything else waits for
            // 'cart:update' as before.
            if (this.features['cart'] && cartSection) {
                const $cartDoc = parseHTML(cartSection);
                this.publish('cart:render', {
                    cart: {
                        getCount: () => $cartDoc.querySelector('[data-cart-counter]').innerText,
                        getItems: () => $cartDoc.querySelector('[data-cart-items]'),
                        getSubtotalBlock: () => $cartDoc.querySelector('[data-cart-subtotal-block]'),
                        getFreeShippingBlock: () => $cartDoc.querySelector('[data-cart-free-shipping-block]'),
                        getHazmatBlock: () => $cartDoc.querySelector('[data-cart-hazmat]')
                    }
                });
            }

            const needsMeta = this.features['cart-total'] || this.features['counter'] || this.features['drawer'];
            const needsDrawer = this.features['drawer'];

            const [meta, drawerSection] = await Promise.all([
                needsMeta ? this._getCartMeta() : null,
                needsDrawer ? this._getDrawerSection() : null
            ]);

            const eventData = {};

            if (needsMeta) eventData.meta = meta;

            if (needsDrawer)
                eventData.drawer = {
                    section: drawerSection,
                    open: !this.features['notification']
                }

            if (this.features['notification'])
                eventData.notification = {
                    section: cartData.sections?.[NOTIFICATION_SECTION],
                    recommendationProductId: cartData.items?.[0]?.product_id
                }

            this.publish('cart:update', eventData)
        }
        
        async _getCartMeta() {
            const res = await fetch(`${Routes('cart')}?view=meta`);
            const { count, ...data } = await res.json();
            return { count: Number(count), ...data };
        }

        async _getDrawerSection() {
            const res = await fetch(`${Routes('root')}?section_id=${SECTION_DRAWER}`);
            return await res.text();
        }

        async _clear() {
            try {
                const res = await fetch('/cart/clear.js', {
                method: 'POST',
                headers: {
                    'Accept': 'application/json'
                }
                });

                if (!res.ok) {
                throw new Error('Failed to clear cart');
                }

                const sectionRes = await fetch(`${Routes('cart')}?sections=${CART_R_SECTION}`);

                if (!sectionRes.ok) {
                throw new Error('Failed to render cart sections');
                }

                const sections = await sectionRes.json();

                const cartData = {
                sections
                };

                await this._publishUpdates(cartData);

            } catch (e) {
                this._handleError(e);
            }
        }

        _requestCart(route, body) {
            return fetch(`${Routes(route)}.js`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(body)
            })
        }

        get isCartPage() {
            return this.hasAttribute(ATTR_IS_CART_PAGE)
        }

        get errorText() {
            return this.getAttribute(ATTR_ERROR_TEXT) || '';
        }

        get redirectToCart() {
            return !this.isCartPage && !this.features['notification'] && !this.features['drawer'];
        }
    });

})();